/**
 * Almacenamiento API — gestión voluntaria de los archivos pesados (fotos,
 * videos y documentos) que viven en el volumen del servidor.
 *
 * Solo administrador y superadmin (lo impone el backend). Todo va por
 * `apiFetch`, que adjunta la autenticación y devuelve `null` ante cualquier
 * error (ya mostró el toast). Por eso cada método puede devolver `null` y el
 * llamador DEBE tratarlo como "no se pudo": nunca como "0 resultados".
 *
 * Contrato (fuente de verdad: la sección A del plan de la ronda "almacenamiento"):
 *   GET  /almacenamiento/resumen
 *   POST /almacenamiento/refrescar
 *   GET  /almacenamiento/archivos
 *   GET  /almacenamiento/archivos/:id
 *   POST /almacenamiento/impacto
 *   POST /almacenamiento/eliminar
 *   GET  /almacenamiento/registro
 *   GET  /almacenamiento/consumo?dias=7|30
 *   POST /almacenamiento/previews/generar   (rellena la versión liviana de los videos existentes)
 *   GET  /almacenamiento/previews/estado    (avance de ese relleno, en memoria)
 */

import { apiFetch } from "./apiClient";

// ─── Tipos del contrato ──────────────────────────────────────────────────────

export type TipoArchivo = "image" | "video" | "doc";
export type EstadoArchivo = "en_uso" | "huerfano" | "roto";
export type EstadoFiltro = "todos" | EstadoArchivo;
export type OrdenArchivos = "peso" | "fecha" | "nombre" | "modulo";
export type DireccionOrden = "asc" | "desc";
export type OrigenFecha = "registrada" | "deducida" | "desconocida";

export interface VolumenInfo {
  totalBytes: number;
  usadoBytes: number;
  libreBytes: number;
}

export interface ResumenPorModulo {
  modulo: string;
  etiqueta: string;
  cantidad: number;
  bytes: number;
}

export interface ResumenPorTipo {
  tipo: TipoArchivo;
  cantidad: number;
  bytes: number;
}

export interface ResumenAlmacenamiento {
  escaneadoEn: string;
  volumen: VolumenInfo | null;
  archivos: { cantidad: number; bytes: number };
  huerfanos: { cantidad: number; bytes: number };
  referenciasRotas: { cantidad: number };
  externos: { cantidad: number };
  porModulo: ResumenPorModulo[];
  porTipo: ResumenPorTipo[];
  /**
   * Videos de 3 MB o más, en uso y sin versión liviana, con el peso de sus originales
   * (lo que se ahorraría reproduciendo la copia). `0` mientras la función esté apagada.
   * Incluye videos que nunca tendrán una (ya son chicos o la copia no ahorra). Opcional:
   * un servidor anterior no lo envía.
   */
  videosSinVersionLiviana?: { cantidad: number; bytes: number };
  masAntiguoISO: string | null;
}

export interface SubidoPor {
  id: number;
  nombre: string;
  rol: string;
  avatar: string | null;
  avatar_url: string | null;
}

export interface UsoArchivo {
  fuente: string;
  modulo: string;
  etiquetaModulo: string;
  filaId: number;
  titulo: string;
  /** Nombres crudos: el frontend aplica `formatPropertyName` al mostrarlos. */
  propiedades: string[];
  /** Ruta de la SPA para abrir al dueño del archivo, p. ej. "/aseos". */
  ruta: string | null;
  visibleParaCliente: boolean;
  /** Advertencia si borrarlo es delicado (documento del cliente, nota privada…). */
  sensible: string | null;
}

export interface Archivo {
  /** Opaco y estable (base64url de la key). */
  id: string;
  key: string;
  carpeta: string;
  nombre: string;
  mime: string | null;
  tipo: TipoArchivo;
  /** Archivo completo + miniatura + versión liviana del video, leído del disco. */
  pesoBytes: number;
  /** La parte de `pesoBytes` que es la miniatura. */
  pesoMiniaturaBytes: number;
  /** La parte de `pesoBytes` que es la versión liviana del video (0 si no tiene). */
  previewBytes: number;
  /**
   * true: la fila ES una versión liviana cuyo video ya no existe (huérfana, se puede
   * eliminar sola). Su `pesoBytes` es el de la copia.
   */
  soloVersionLiviana: boolean;
  fecha: string | null;
  fechaOrigen: OrigenFecha;
  subidoPor: SubidoPor | null;
  estado: EstadoArchivo;
  url: string | null;
  thumbUrl: string | null;
  /**
   * URL firmada de la versión liviana del video, si tiene (el backend la firma cuando
   * `previewBytes > 0`). Opcional: sin ella la pantalla solo ofrece el original. El visor de
   * Almacenamiento la pasa a <VideoPlayer> para abrir el video en la versión liviana.
   */
  previewUrl?: string | null;
  usos: UsoArchivo[];
}

export interface ListaArchivos {
  success: boolean;
  data: Archivo[];
  total: number;
  page: number;
  size: number;
  totalBytesFiltrados: number;
}

export interface ParamsArchivos {
  q?: string;
  modulo?: string;
  tipo?: TipoArchivo;
  propiedadId?: number;
  /** YYYY-MM-DD sobre la fecha de subida (hora Chile). */
  desde?: string;
  hasta?: string;
  antiguedadMeses?: number;
  pesoMinMB?: number;
  estado?: EstadoArchivo;
  orden?: OrdenArchivos;
  dir?: DireccionOrden;
  page?: number;
  size?: number;
}

export interface AdvertenciaImpacto {
  codigo: string;
  mensaje: string;
  cantidad: number;
}

export interface ImpactoEliminacion {
  archivos: number;
  bytes: number;
  huerfanos: number;
  usos: number;
  porModulo: Array<{
    modulo: string;
    etiqueta: string;
    archivos: number;
    bytes: number;
  }>;
  advertencias: AdvertenciaImpacto[];
  rangoFechas: { desde: string; hasta: string } | null;
  noEncontrados: string[];
  /**
   * Seleccionados que NO se eliminarán ahora (video optimizándose, subido hace
   * menos de 5 minutos, miniatura de un archivo que existe), con su motivo.
   * Opcional: un servidor anterior no lo envía.
   */
  omitidos?: OmitidoImpacto[];
  requiereConfirmacionEscrita: boolean;
}

export interface OmitidoImpacto {
  id: string;
  nombre: string;
  motivo: string;
}

export interface FallidoEliminacion {
  id: string;
  nombre: string;
  motivo: string;
}

export interface ResultadoEliminacion {
  eliminados: number;
  bytesLiberados: number;
  fallidos: FallidoEliminacion[];
  yaNoExistian: number;
  registroIds: number[];
}

export interface UsoRegistro {
  modulo: string;
  etiquetaModulo: string;
  titulo: string;
  propiedades: string[];
}

export interface RegistroEliminado {
  id: number;
  creadoEn: string;
  usuarioId: number | null;
  usuarioNombre: string | null;
  nombre: string;
  key: string;
  mime: string | null;
  tipo: string;
  pesoBytes: number;
  fechaSubida: string | null;
  huerfano: boolean;
  usos: UsoRegistro[];
  quedoHuerfano: boolean;
}

export interface ParamsRegistro {
  page?: number;
  size?: number;
  q?: string;
  desde?: string;
  hasta?: string;
}

export interface ListaRegistro {
  success: boolean;
  data: RegistroEliminado[];
  total: number;
  page: number;
  size: number;
}

// ─── Consumo de datos de /files (round 2026-10-27) ──────────────────────────

/**
 * Lo que mide el consumo: las miniaturas cuentan aparte de las fotos y la versión liviana
 * (`preview`) aparte del video original.
 */
export type TipoConsumo = "image" | "thumb" | "video" | "doc" | "preview";
export type DiasConsumo = 7 | 30;

export interface ConsumoPorTipo {
  tipo: TipoConsumo;
  bytes: number;
  peticiones: number;
}

export interface ConsumoPorDia {
  /** YYYY-MM-DD (día chileno). La serie viene completa: los días sin consumo traen 0. */
  fecha: string;
  bytes: number;
}

export interface ConsumoArchivoTop {
  clave: string;
  tipo: TipoConsumo;
  bytes: number;
  peticiones: number;
  /** Del índice de Almacenamiento si está en caché; si no, deducido de la key. */
  nombre: string | null;
  modulo: string | null;
}

/**
 * Bytes servidos por `/files` (fotos, videos y documentos). NO incluye la API
 * ni las subidas. Un 304 cuenta una petición y 0 bytes.
 */
export interface ConsumoAlmacenamiento {
  desde: string;
  hasta: string;
  dias: DiasConsumo;
  totalBytes: number;
  totalPeticiones: number;
  porTipo: ConsumoPorTipo[];
  porDia: ConsumoPorDia[];
  /** Los 15 más servidos, de mayor a menor peso entregado. */
  top: ConsumoArchivoTop[];
  /** Aclaración de alcance, lista para mostrar. */
  nota: string;
}

// ─── Versiones livianas de los videos (round 2026-10-27) ─────────────────────

/** Avance del relleno (`GET /previews/estado`). Vive en la memoria del servidor. */
export interface EstadoPreviews {
  enCurso: boolean;
  /** Videos que faltan por procesar en la corrida actual. */
  pendientes: number;
  generadas: number;
  fallidas: number;
  /** Videos que, al probarlos, no necesitan versión liviana (ya son chicos o la copia no ahorra). */
  omitidas: number;
  /** Videos que se encolaron en la última corrida. */
  total: number;
  iniciadoEn: string | null;
  terminadoEn: string | null;
}

/**
 * Respuesta de `POST /previews/generar`. Si ya había un relleno corriendo, además trae el
 * estado de ese (`enCurso: true` y los campos de `EstadoPreviews`).
 */
export type ResultadoGenerarPreviews = {
  encolados: number;
  yaTenian: number;
  omitidos: number;
  enCurso: boolean;
} & Partial<EstadoPreviews>;

interface Envolvente<T> {
  success: boolean;
  data: T;
}

/** Palabra que el backend exige SIEMPRE para eliminar. */
export const PALABRA_CONFIRMACION = "ELIMINAR";
/** Máximo de ids por request de impacto / eliminación. */
export const MAX_IDS_POR_OPERACION = 500;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Arma `?a=1&b=2` descartando `undefined`, `null` y cadenas vacías. */
export function armarQuery(params?: object): string {
  if (!params) return "";
  const pares = Object.entries(params as Record<string, unknown>)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => [k, String(v)] as [string, string]);
  return pares.length ? "?" + new URLSearchParams(pares).toString() : "";
}

// ─── almacenamientoApi ───────────────────────────────────────────────────────

export const almacenamientoApi = {
  /** GET /almacenamiento/resumen — salud del espacio (usa la caché del último escaneo). */
  resumen: (): Promise<Envolvente<ResumenAlmacenamiento> | null> =>
    apiFetch<Envolvente<ResumenAlmacenamiento>>("/almacenamiento/resumen", {
      errorMsg: "No se pudo leer el resumen de almacenamiento",
    }),

  /** POST /almacenamiento/refrescar — vuelve a escanear el volumen. */
  refrescar: (): Promise<Envolvente<ResumenAlmacenamiento> | null> =>
    apiFetch<Envolvente<ResumenAlmacenamiento>>("/almacenamiento/refrescar", {
      method: "POST",
      errorMsg: "No se pudo reescanear el almacenamiento",
    }),

  /** GET /almacenamiento/archivos — página filtrable de archivos. */
  archivos: (params?: ParamsArchivos): Promise<ListaArchivos | null> =>
    apiFetch<ListaArchivos>(`/almacenamiento/archivos${armarQuery(params)}`, {
      errorMsg: "No se pudo obtener la lista de archivos",
    }),

  /** GET /almacenamiento/archivos/:id */
  archivo: (id: string): Promise<Envolvente<Archivo> | null> =>
    apiFetch<Envolvente<Archivo>>(
      `/almacenamiento/archivos/${encodeURIComponent(id)}`,
      { errorMsg: "No se pudo obtener el archivo" },
    ),

  /**
   * POST /almacenamiento/impacto — qué se llevaría eliminar estos archivos.
   * Es de solo lectura. `null` = no se pudo calcular (NO es "sin impacto").
   */
  impacto: (ids: string[]): Promise<Envolvente<ImpactoEliminacion> | null> =>
    apiFetch<Envolvente<ImpactoEliminacion>>("/almacenamiento/impacto", {
      method: "POST",
      body: JSON.stringify({ ids }),
      errorMsg: "No se pudo calcular el impacto de la eliminación",
    }),

  /** POST /almacenamiento/eliminar — borrado definitivo (confirmación obligatoria). */
  eliminar: (ids: string[]): Promise<Envolvente<ResultadoEliminacion> | null> =>
    apiFetch<Envolvente<ResultadoEliminacion>>("/almacenamiento/eliminar", {
      method: "POST",
      body: JSON.stringify({ ids, confirmacion: PALABRA_CONFIRMACION }),
      errorMsg: "No se pudo completar la eliminación",
    }),

  /** GET /almacenamiento/consumo — bytes servidos por /files en los últimos 7 o 30 días. */
  consumo: (dias: DiasConsumo = 7): Promise<Envolvente<ConsumoAlmacenamiento> | null> =>
    apiFetch<Envolvente<ConsumoAlmacenamiento>>(`/almacenamiento/consumo${armarQuery({ dias })}`, {
      errorMsg: "No se pudo obtener el consumo de datos",
    }),

  /**
   * POST /almacenamiento/previews/generar — encola la versión liviana de los videos que no la
   * tienen. No espera: el avance se sigue con `estadoPreviews`. `silent`: la pantalla explica
   * ella misma el fallo (p. ej. 409 PREVIEW_DESACTIVADA) en vez de mostrar un aviso genérico.
   * `null` = no se pudo.
   */
  generarPreviews: (): Promise<Envolvente<ResultadoGenerarPreviews> | null> =>
    apiFetch<Envolvente<ResultadoGenerarPreviews>>("/almacenamiento/previews/generar", {
      method: "POST",
      silent: true,
    }),

  /** GET /almacenamiento/previews/estado — sondeo en segundo plano, sin avisos de error. */
  estadoPreviews: (): Promise<Envolvente<EstadoPreviews> | null> =>
    apiFetch<Envolvente<EstadoPreviews>>("/almacenamiento/previews/estado", { silent: true }),

  /** GET /almacenamiento/registro — la memoria de lo que se eliminó. */
  registro: (params?: ParamsRegistro): Promise<ListaRegistro | null> =>
    apiFetch<ListaRegistro>(`/almacenamiento/registro${armarQuery(params)}`, {
      errorMsg: "No se pudo obtener el registro de eliminados",
    }),
};
